// GL journal entry (#5024) — mirrors Finance.Models JournalEntryDto / JournalLineItemDto.
import { Currency } from '../ap-document/ap-document';
import { PostingModule, SubledgerType } from '../financial-account/financial-account';

// The module that posted an entry is the same enum the chart of accounts uses to authorize posting, so it
// is re-exported here rather than declared twice — a second copy would drift the moment a module is added.
export { PostingModule };

/** Mirrors Finance.Models JournalEntryStatus. */
export enum JournalEntryStatus {
  /** Borrador — not yet posted, balances not affected. */
  Draft = 1,
  /** Contabilizado — balances updated. */
  Posted = 2,
  /** Revertido — this document has been reversed by another. */
  Reversed = 3,
  /** Asiento de Reversión — created to reverse another document. */
  ReversingEntry = 4,
}

export interface SubledgerReference {
  type: SubledgerType;
  referenceId: string;
  code?: string | null;
}

export interface JournalLineItem {
  lineNumber: number;
  accountReference: string;
  accountNumber: string;
  accountName: string;
  debitAmount: number;
  creditAmount: number;
  /** The transaction currency THIS line was captured in (#5144) — usually the entry's, but a
   *  per-line-rate entry debits Lempiras and credits dollars in one posting. */
  currency?: number;
  /** The transaction → functional multiplier this line converted at — its own, per line (#5144). */
  exchangeRate?: number;
  /** #5028 — the line restated in the company's functional currency (#4549). */
  functionalDebitAmount: number;
  functionalCreditAmount: number;
  description?: string | null;
  costCenter?: string | null;
  taxCode?: string | null;
  subledger?: SubledgerReference | null;
}

export interface JournalEntry {
  id?: string | null;
  number: string;
  documentDate: string;
  postingDate: string;
  fiscalYear: number;
  fiscalPeriod: number;
  financialCompanyReference: string;
  businessReference?: string | null;
  description: string;
  /** The source document's number (e.g. AP-HS-2026-000142) — a bare string until PBI #5028 item 2 lands. */
  reference?: string | null;
  currency: Currency;
  /** #5028 — the books' currency and the rate this entry was converted at. */
  functionalCurrency: Currency;
  exchangeRate: number;
  /** #5028 — which module posted the entry; drives the Source chip and the dashboard's source filter. */
  sourceModule: PostingModule;
  status: JournalEntryStatus;
  reversedByDocumentNumber?: string | null;
  reversesDocumentNumber?: string | null;
  lineItems: JournalLineItem[];
  totalDebits: number;
  totalCredits: number;
  /** #5028 — the same totals in functional currency, for the Balanced footer's conversion line. */
  totalFunctionalDebits: number;
  totalFunctionalCredits: number;
  createdBy?: string | null;
  createdDate?: string | null;
  modifiedBy?: string | null;
  modifiedDate?: string | null;
}

/**
 * #5028 — filters for `POST /journal-entries/search`. Company scope is NOT sent: the API defaults it to the
 * company that owns the current site, so the dashboard can never accidentally read another company's ledger.
 */
export interface JournalEntrySearchCommand {
  documentNumber?: string | null;
  reference?: string | null;
  status?: JournalEntryStatus | number | null;
  postingDateFrom?: string | null;
  postingDateTo?: string | null;
  fiscalYear?: number | null;
  fiscalPeriod?: number | null;
  accountReference?: string | null;
  sourceModule?: PostingModule | number | null;
  /** One box for the entry number, the source document reference or the description. */
  searchText?: string | null;
}

/** #5028 — the dashboard's four tiles, in one call (`GET /journal-entries/summary`). */
export interface JournalEntrySummary {
  functionalCurrency: Currency;
  postedCount: number;
  draftCount: number;
  reversedCount: number;
  /** Total functional debits across posted entries — debits only, or a balanced entry counts twice. */
  postedVolume: number;
}

/** One debit-or-credit line on a manual entry create/edit request — mirrors Finance.API `JournalEntryLineInsert`. */
export interface JournalEntryLineInsert {
  accountReference: string;
  debitAmount: number;
  creditAmount: number;
  description?: string | null;
}

/**
 * Create/edit payload for a manual journal entry (#5077) — mirrors Finance.API `JournalEntryInsert`.
 *
 * Deliberately much smaller than {@link JournalEntry}: the number, status, source module, fiscal period,
 * company and every functional-currency figure are the server's to decide, and sending them would only
 * create a way for the client to disagree with the ledger. There is no currency and no document date
 * either — a manual entry is captured in dollars, and the posting date is the only date it has.
 */
export interface JournalEntryInsert {
  /** ISO date. Drives the fiscal year and period, so the accounting period it lands in must be open. */
  postingDate: string;
  description: string;
  reference?: string | null;
  lineItems: JournalEntryLineInsert[];
}
